/**
 * Reptilian Agent - Browser entry point
 * Connects headless core + view + systems
 */

import './styles.css';
import { createGameCore, COUNTRIES, isAllConquered } from './game/index.js';
import { calculateOfflineIp } from './game/systems/offline.js';
import { t, getCurrentLocale, setLocale, SUPPORTED_LOCALES, type Locale } from './i18n/index.js';
import { formatNumber } from './utils/format.js';
import {
  initRenderer, requestRender, setViewState, showToast, markScreenDirty, showConquestPopup,
} from './view/renderer.js';
import type { Tab } from './view/renderer.js';
import { initMainScreen } from './view/mainScreen.js';
import { initUpgradeScreen } from './view/upgradeScreen.js';
import { initPrestigeScreen } from './view/prestigeScreen.js';
import { initAchievementScreen } from './view/achievementScreen.js';
import { initSettingsScreen } from './view/settingsScreen.js';
import { initMissionScreen } from './view/missionScreen.js';
import { initDominationScreen } from './view/dominationScreen.js';
import { initAgentScreen } from './view/agentScreen.js';
import { initInfiltrationScreen } from './view/infiltrationScreen.js';
import { initFactionEventPopup, showFactionEvent, hideFactionEvent } from './view/factionEventPopup.js';
import { initFactionIndicator } from './view/factionIndicator.js';
import { initShadowContact, cleanupShadowContact } from './view/shadowContactPopup.js';
import { FACTION_EVENTS } from './game/index.js';
import { showBossOverlay, isBossOverlayVisible, closeBossOverlay } from './view/bossScreen.js';
import { showBossWarning, showBossTutorial } from './view/bossWarning.js';
import { initSound, playSfx } from './sound.js';
import { AdManager } from './monetization/index.js';
import { createGameAPI } from '@app-games/api';

// === Constants ===
const SAVE_KEY = 'reptilian-agent-save';
const SAVE_INTERVAL = 5000;
const TICK_INTERVAL = 100;
const GOLDEN_CHECK_INTERVAL = 1000;
const ACH_CHECK_INTERVAL = 3000;
const MISSION_CHECK_INTERVAL = 60_000; // check daily mission refresh every 60s
const CONQUEST_CHECK_INTERVAL = 5000; // ending check every 5s
const BOSS_CHECK_INTERVAL = 30_000; // boss spawn check every 30s

// Detect demo mode from URL
const isDemoMode = new URLSearchParams(window.location.search).get('demo') === '1';

// Guard to prevent duplicate visibilitychange listeners
let visibilityListenerRegistered = false;

// === Timer Management ===
// All setInterval handles are tracked so they can be cleared on game reset
const activeIntervals: ReturnType<typeof setInterval>[] = [];

/** Register a setInterval and track it for cleanup */
function managedInterval(fn: () => void, ms: number): ReturnType<typeof setInterval> {
  const id = setInterval(fn, ms);
  activeIntervals.push(id);
  return id;
}

/** Clear all managed intervals */
function clearAllIntervals(): void {
  for (const id of activeIntervals) {
    clearInterval(id);
  }
  activeIntervals.length = 0;
}

// === Init ===
async function main() {
  const core = createGameCore();

  // Skip save load in demo mode
  if (!isDemoMode) {
    loadState(core);
  }

  // Init renderer
  initRenderer(core);

  // Build DOM
  buildLayout();

  // Init view modules
  initMainScreen();
  initUpgradeScreen();
  initPrestigeScreen();
  initAchievementScreen();
  initMissionScreen();
  initDominationScreen();
  initAgentScreen();
  initInfiltrationScreen();
  initFactionEventPopup(
    (action) => { core.dispatch(action); requestRender(); },
    () => core.getState(),
  );
  initShadowContact(
    (action) => { core.dispatch(action); requestRender(); },
    () => core.getState(),
  );
  initSettingsScreen();

  // Init faction indicator (in hud-sub-row, next to rank)
  const hudSubRow = document.querySelector('.hud-sub-row');
  if (hudSubRow) {
    const factionIndicatorResult = initFactionIndicator(
      hudSubRow as HTMLElement,
      () => core.getState(),
    );
    managedInterval(() => factionIndicatorResult.update(), 1000);
  }

  // Init sound
  initSound();

  // Init ads (skip in demo mode)
  if (!isDemoMode) {
    try {
      await AdManager.initialize('auto', {});
    } catch (e) {
      console.warn('Ad init failed:', e);
    }
  }

  // Expose GameAPI for debugging
  try {
    const api = createGameAPI(core as any, { exposeGlobal: true, globalName: 'GameAPI' });
    (window as any).GameAPI = api;
  } catch { /* */ }

  // Debug: set rank for preview
  (window as any).setRank = (grade: string) => {
    const ranks: Record<string, { ip: number; prestige: number }> = {
      'E': { ip: 0, prestige: 0 },
      'D': { ip: 10000, prestige: 0 },
      'C': { ip: 1000000, prestige: 0 },
      'B': { ip: 1000000000, prestige: 0 },
      'A': { ip: 1000000000000, prestige: 0 },
      'S': { ip: 1000000000000, prestige: 1 },
      'SS': { ip: 1000000000000, prestige: 5 },
      'SSS': { ip: 1000000000000, prestige: 15 },
    };
    const r = ranks[grade.toUpperCase()];
    if (!r) {
      console.log('사용법: setRank("E") ~ setRank("SSS")');
      console.log('가능한 랭크: E, D, C, B, A, S, SS, SSS');
      return;
    }
    const s = core.getState() as any;
    s.totalIpEarned = r.ip;
    s.prestigeCount = r.prestige;
    requestRender();
    console.log(`랭크 변경: ${grade.toUpperCase()}`);
  };
  (window as any).addIP = (amount: number) => {
    core.dispatch({ type: 'ADD_IP', payload: { amount } });
    requestRender();
    console.log(`+${amount} IP 추가됨 (현재: ${core.getState().ip})`);
  };
  console.log('[치트] setRank("E") ~ setRank("SSS") 로 캐릭터 미리보기');
  console.log('[치트] addIP(10000000000) 로 100억 IP 추가');

  // Expose restartGameLoop for use by settings reset
  (window as any).__restartGameLoop = () => {
    startGameLoop(core);
  };

  // Check offline income (skip in demo mode)
  if (!isDemoMode) {
    checkOfflineIncome(core);
  }

  // Init daily missions
  core.dispatch({ type: 'REFRESH_DAILY_MISSIONS' });

  // Start game loops
  startGameLoop(core);

  // Initial render
  setViewState({ currentTab: 'main' });
  requestRender();

  // === Demo Mode (developer-only, never exposed to users) ===
  // Usage: window.__demo.start() / window.__demo.stop()
  // Or URL: ?demo=1
  {
    const { DemoPlayer } = await import('./demo/DemoPlayer.js');
    const demoPlayer = new DemoPlayer();

    // Helper: switch tab programmatically (for DEMO_TAB commands)
    const switchTab = (tab: string) => {
      setViewState({ currentTab: tab as Tab });
      markScreenDirty();
      requestRender();

      // Update tab bar active states
      const tabBar = document.getElementById('tab-bar');
      if (tabBar) {
        tabBar.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
        const activeBtn = tabBar.querySelector(`[data-tab="${tab}"]`);
        if (activeBtn) activeBtn.classList.add('active');
      }
    };

    // Dispatch function: handles game actions + DEMO_TAB
    const demoDispatch = (action: Record<string, unknown>) => {
      if (action.type === 'DEMO_TAB') {
        const payload = action.payload as { tab: string } | undefined;
        if (payload?.tab) {
          switchTab(payload.tab);
        }
        return;
      }
      core.dispatch(action as any);
      requestRender();
    };

    // Start demo: reset state + play
    const startDemo = () => {
      core.dispatch({ type: 'RESET' });
      setViewState({ currentTab: 'main' });
      markScreenDirty();
      requestRender();
      demoPlayer.play(demoDispatch);
    };

    // Load demo data
    try {
      const resp = await fetch('./demo-data/demo-01.json');
      const data = await resp.json();
      demoPlayer.load(data);
      demoPlayer.setResetFn(() => {
        core.dispatch({ type: 'RESET' });
        setViewState({ currentTab: 'main' });
        markScreenDirty();
        requestRender();
      });
    } catch {
      console.warn('[Demo] demo-01.json not found');
    }

    (window as any).__demo = {
      start: startDemo,
      stop: () => demoPlayer.stop(),
      isPlaying: () => demoPlayer.isPlaying(),
    };

    // URL ?demo=1 auto-start
    if (isDemoMode) {
      startDemo();
    }
  }

  console.log('[ReptilianAgent] Game started');
}

// === Layout ===
function buildLayout() {
  const app = document.getElementById('app');
  if (!app) return;

  app.innerHTML = `
    <div class="hud">
      <div class="hud-left" data-currency="ip">
        <div class="hud-ip-row">
          <span class="hud-ip-icon">🛸</span>
          <span class="hud-ip" id="hud-ip">0</span>
        </div>
        <div class="hud-cps" id="hud-cps">0/s</div>
      </div>
      <div class="hud-right">
        <div class="hud-currency" data-currency="gc">
          <span class="hud-currency-icon">💎</span>
          <span class="hud-gc" id="hud-gc">0</span>
        </div>
        <div class="hud-currency" data-currency="cores">
          <span class="hud-currency-icon">🔮</span>
          <span class="hud-cores" id="hud-cores">0</span>
        </div>
        <div class="hud-currency" data-currency="docs">
          <span class="hud-currency-icon">📜</span>
          <span class="hud-docs" id="hud-docs">0</span>
        </div>
      </div>
    </div>
    <div class="hud-sub-row">
      <div class="hud-rank" id="hud-rank"></div>
      <select class="hud-lang-select" id="hud-lang-select">
        ${SUPPORTED_LOCALES.map(loc => `<option value="${loc}" ${loc === getCurrentLocale() ? 'selected' : ''}>${loc.toUpperCase()}</option>`).join('')}
      </select>
    </div>
    <div class="boost-bar" id="boost-bar"></div>
    <div class="screen-bg" id="screen-bg"></div>
    <div class="screen-container" id="screen-content"></div>
    <div class="tab-bar" id="tab-bar">
      <button class="tab-btn active" data-tab="main">
        <span class="tab-icon">\u{1F40D}</span>
        <span>${t('tab_main')}</span>
      </button>
      <button class="tab-btn" data-tab="upgrade">
        <span class="tab-icon">\u{2B06}</span>
        <span>${t('tab_upgrade')}</span>
      </button>
      <button class="tab-btn" data-tab="prestige">
        <span class="tab-icon">\u{2B50}</span>
        <span>${t('tab_prestige')}</span>
      </button>
      <button class="tab-btn" data-tab="achievement">
        <span class="tab-icon">\u{1F3C6}</span>
        <span>${t('tab_achievement')}</span>
      </button>
      <button class="tab-btn" data-tab="mission">
        <span class="tab-icon">\u{1F4CB}</span>
        <span>${t('tab_mission')}</span>
      </button>
      <button class="tab-btn" data-tab="domination">
        <span class="tab-icon">\u{1F30D}</span>
        <span>${t('tab_domination')}</span>
      </button>
      <button class="tab-btn" data-tab="agent">
        <span class="tab-icon">\u{1F575}</span>
        <span>${t('tab_agent')}</span>
      </button>
      <button class="tab-btn" data-tab="infiltration">
        <span class="tab-icon infil-tab-icon"></span>
        <span>${t('tab_infiltration')}</span>
      </button>
      <button class="tab-btn" data-tab="settings">
        <span class="tab-icon">\u{2699}</span>
        <span>${t('tab_settings')}</span>
      </button>
    </div>
  `;

  // Currency tooltip on tap
  document.querySelectorAll('[data-currency]').forEach(el => {
    el.addEventListener('click', () => {
      const currency = (el as HTMLElement).dataset.currency!;
      showCurrencyTooltip(currency);
    });
  });

  // Language selector
  const langSelect = document.getElementById('hud-lang-select');
  if (langSelect) {
    langSelect.addEventListener('change', (e) => {
      const newLocale = (e.target as HTMLSelectElement).value as Locale;
      setLocale(newLocale);
      location.reload();
    });
  }

  // Tab handlers — use pointerup for faster response on mobile
  const tabBar = document.getElementById('tab-bar');
  if (tabBar) {
    tabBar.querySelectorAll('[data-tab]').forEach(btn => {
      let pointerStartY = 0;
      btn.addEventListener('pointerdown', (e) => {
        pointerStartY = (e as PointerEvent).clientY;
      });
      btn.addEventListener('pointerup', (e) => {
        // Ignore if finger moved too far (scroll intent)
        if (Math.abs((e as PointerEvent).clientY - pointerStartY) > 10) return;
        const tab = (btn as HTMLElement).dataset.tab! as Tab;
        setViewState({ currentTab: tab });

        // Update tab active states
        tabBar.querySelectorAll('.tab-btn').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });
  }
}

// === Currency Tooltip ===
function showCurrencyTooltip(currency: string) {
  const existing = document.querySelector('.currency-tooltip-overlay');
  if (existing) existing.remove();

  const info = {
    ip: {
      icon: '🛸',
      name: t('currency_ip_name'),
      earn: t('currency_ip_earn'),
      use: t('currency_ip_use'),
      color: 'var(--accent)',
    },
    gc: {
      icon: '💎',
      name: t('currency_gc_name'),
      earn: t('currency_gc_earn'),
      use: t('currency_gc_use'),
      color: 'var(--gc-color)',
    },
    cores: {
      icon: '🔮',
      name: t('currency_cores_name'),
      earn: t('currency_cores_earn'),
      use: t('currency_cores_use'),
      color: 'var(--core-color)',
    },
    docs: {
      icon: '📜',
      name: t('currency_docs_name'),
      earn: t('currency_docs_earn'),
      use: t('currency_docs_use'),
      color: 'var(--doc-color)',
    },
  }[currency];

  if (!info) return;

  const overlay = document.createElement('div');
  overlay.className = 'currency-tooltip-overlay';
  overlay.innerHTML = `
    <div class="currency-tooltip">
      <div class="currency-tooltip-header" style="color:${info.color}">
        <span style="font-size:24px">${info.icon}</span>
        <span>${info.name}</span>
      </div>
      <div class="currency-tooltip-section">
        <div class="currency-tooltip-label">${t('currency_how_earn')}</div>
        <div class="currency-tooltip-text">${info.earn}</div>
      </div>
      <div class="currency-tooltip-section">
        <div class="currency-tooltip-label">${t('currency_how_use')}</div>
        <div class="currency-tooltip-text">${info.use}</div>
      </div>
    </div>
  `;

  overlay.addEventListener('click', () => overlay.remove());
  document.body.appendChild(overlay);

  // Auto-close after 5s
  setTimeout(() => overlay.remove(), 5000);
}

// === Game Loops ===
function startGameLoop(core: ReturnType<typeof createGameCore>) {
  // Clear any previous intervals (important for game reset)
  clearAllIntervals();

  // Clean up boss overlay, popups, and shadow contact from previous session
  closeBossOverlay();
  hideFactionEvent();
  cleanupShadowContact();

  // Re-initialize shadow contact (its checkHandle was cleared by cleanup)
  initShadowContact(
    (action) => { core.dispatch(action); requestRender(); },
    () => core.getState(),
  );

  // Remove stale golden event element
  if (goldenEl) {
    goldenEl.remove();
    goldenEl = null;
  }

  let lastTime = Date.now();

  // Track conquered countries for completion detection
  let prevConqueredSet = new Set(
    Object.entries(core.getState().conquest.countries)
      .filter(([, v]) => v.conquered)
      .map(([k]) => k)
  );

  // Main tick
  managedInterval(() => {
    const now = Date.now();
    const delta = now - lastTime;
    lastTime = now;
    core.dispatch({ type: 'TICK', payload: { deltaMs: delta } });

    // Check for newly conquered countries
    const st = core.getState();
    for (const [countryId, cs] of Object.entries(st.conquest.countries)) {
      if (cs.conquered && !prevConqueredSet.has(countryId)) {
        prevConqueredSet.add(countryId);
        const country = COUNTRIES.find(c => c.id === countryId);
        if (country) {
          playSfx('conquest');
          showConquestPopup(country.flag, t(country.nameKey));
          markScreenDirty();
        }
      }
    }

    requestRender();
  }, TICK_INTERVAL);

  // Golden event check
  managedInterval(() => {
    const state = core.getState();
    const now = Date.now();

    // Try to spawn
    if (!state.currentGoldenEvent && now >= state.nextGoldenEventAt) {
      core.dispatch({ type: 'SPAWN_GOLDEN_EVENT' });
      markScreenDirty();
    }

    // Render golden event if present
    renderGoldenEvent(core);
  }, GOLDEN_CHECK_INTERVAL);

  // Achievement check
  managedInterval(() => {
    core.dispatch({ type: 'CHECK_ACHIEVEMENTS' });
    const state = core.getState();
    // Check for newly unlocked
    const newlyUnlocked = Object.entries(state.achievements)
      .filter(([, v]) => v.unlocked && v.unlockedAt && Date.now() - v.unlockedAt < ACH_CHECK_INTERVAL + 500);
    if (newlyUnlocked.length > 0) {
      markScreenDirty();
      requestRender();
    }
    for (const [id] of newlyUnlocked) {
      const key = 'ach_' + id.toLowerCase();
      showToast(t(key), t('ach_unlock_label'));
      playSfx('achievement');
    }
  }, ACH_CHECK_INTERVAL);

  // Daily mission refresh check (handles day rollover)
  managedInterval(() => {
    const prevDate = core.getState().dailyMissionDate;
    core.dispatch({ type: 'REFRESH_DAILY_MISSIONS' });
    // Only force re-render if missions actually changed (day rolled over)
    if (core.getState().dailyMissionDate !== prevDate) {
      markScreenDirty();
      requestRender();
    }
  }, MISSION_CHECK_INTERVAL);

  // Track mission-relevant state changes
  let prevTapCount = core.getState().tapCount;
  let prevProducerTotal = countProducers(core.getState());
  let prevTotalIpEarned = core.getState().totalIpEarned;
  let prevGoldenTouched = core.getState().goldenEventsTouched;
  let prevMaxCombo = core.getState().maxCombo;

  managedInterval(() => {
    const st = core.getState();
    let missionUpdated = false;

    // Track taps
    const tapDelta = st.tapCount - prevTapCount;
    if (tapDelta > 0) {
      core.dispatch({ type: 'UPDATE_MISSION_PROGRESS', payload: { missionType: 'tap', amount: tapDelta } });
      prevTapCount = st.tapCount;
      missionUpdated = true;
    }

    // Track producer purchases
    const prodTotal = countProducers(st);
    const prodDelta = prodTotal - prevProducerTotal;
    if (prodDelta > 0) {
      core.dispatch({ type: 'UPDATE_MISSION_PROGRESS', payload: { missionType: 'buyProducer', amount: prodDelta } });
      prevProducerTotal = prodTotal;
      missionUpdated = true;
    }

    // Track IP earned
    const ipDelta = st.totalIpEarned - prevTotalIpEarned;
    if (ipDelta > 0) {
      core.dispatch({ type: 'UPDATE_MISSION_PROGRESS', payload: { missionType: 'earnIp', amount: ipDelta } });
      prevTotalIpEarned = st.totalIpEarned;
      missionUpdated = true;
    }

    // Track golden events
    const goldenDelta = st.goldenEventsTouched - prevGoldenTouched;
    if (goldenDelta > 0) {
      core.dispatch({ type: 'UPDATE_MISSION_PROGRESS', payload: { missionType: 'goldenEvent', amount: goldenDelta } });
      prevGoldenTouched = st.goldenEventsTouched;
      missionUpdated = true;
    }

    // Track max combo
    const comboDelta = st.maxCombo - prevMaxCombo;
    if (comboDelta > 0) {
      core.dispatch({ type: 'UPDATE_MISSION_PROGRESS', payload: { missionType: 'combo', amount: comboDelta } });
      prevMaxCombo = st.maxCombo;
      missionUpdated = true;
    }

    if (missionUpdated) {
      requestRender();
    }
  }, 1000); // check every second

  // Conquest: ending check
  managedInterval(() => {
    const st = core.getState();
    if (st.conquest.endingUnlocked) return;

    if (isAllConquered(st)) {
      core.dispatch({ type: 'UNLOCK_ENDING' });
      markScreenDirty();
      requestRender();
    }
  }, CONQUEST_CHECK_INTERVAL);

  // Boss spawn check
  managedInterval(() => {
    const st = core.getState();
    // Don't spawn if already fighting a boss
    if (st.boss.current) return;

    const now = Date.now();
    if (now >= st.boss.nextBossAt) {
      core.dispatch({ type: 'SPAWN_BOSS' });
      const newSt = core.getState();
      if (newSt.boss.current && !isBossOverlayVisible()) {
        showBossWarning(() => {
          // Show tutorial on first boss
          if (newSt.boss.totalBossesDefeated === 0 && newSt.boss.totalBossesExpired === 0) {
            showBossTutorial(() => showBossOverlay());
          } else {
            showBossOverlay();
          }
        });
      }
    }
  }, BOSS_CHECK_INTERVAL);

  // Faction event check
  const FACTION_EVENT_CHECK_INTERVAL = 5_000;
  managedInterval(() => {
    const st = core.getState();
    const now = Date.now();

    // Expire current event
    if (st.faction.currentEvent && now > st.faction.currentEvent.expiresAt) {
      core.dispatch({ type: 'IGNORE_FACTION_EVENT' });
      hideFactionEvent();
    }

    // Spawn new event
    if (!st.faction.currentEvent && now >= st.faction.nextEventAt) {
      core.dispatch({ type: 'SPAWN_FACTION_EVENT' });
      const st2 = core.getState();
      if (st2.faction.currentEvent) {
        const evtDef = FACTION_EVENTS.find(e => e.id === st2.faction.currentEvent!.eventId);
        if (evtDef) showFactionEvent(evtDef);
      }
    }
  }, FACTION_EVENT_CHECK_INTERVAL);

  // Auto-save (skip in demo mode)
  if (!isDemoMode) {
    managedInterval(() => saveState(core), SAVE_INTERVAL);
  }

  // Save on page hide (uses document listener — only register once)
  if (!visibilityListenerRegistered) {
    visibilityListenerRegistered = true;
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) saveState(core);
    });
  }
}

function countProducers(state: ReturnType<ReturnType<typeof createGameCore>['getState']>): number {
  return Object.values(state.producers).reduce((sum, p) => sum + p.count, 0);
}

// === Golden Event UI ===
let goldenEl: HTMLElement | null = null;

function renderGoldenEvent(core: ReturnType<typeof createGameCore>) {
  const state = core.getState();
  const evt = state.currentGoldenEvent;
  const now = Date.now();

  if (!evt || now > evt.expiresAt) {
    if (goldenEl) {
      goldenEl.remove();
      goldenEl = null;
    }
    return;
  }

  if (!goldenEl) {
    goldenEl = document.createElement('div');
    goldenEl.className = 'golden-event';
    document.body.appendChild(goldenEl);

    goldenEl.addEventListener('click', () => {
      core.dispatch({ type: 'TOUCH_GOLDEN_EVENT' });
      playSfx('goldenEvent');
      showToast(t('golden_' + evt.type));
      requestRender();
    });
  }

  const remaining = Math.ceil((evt.expiresAt - now) / 1000);
  const eventKey = 'golden_' + evt.type;
  goldenEl.textContent = `${t(eventKey)} (${remaining}s) - ${t('golden_touch')}`;
}

// === Offline Income ===
function checkOfflineIncome(core: ReturnType<typeof createGameCore>) {
  const state = core.getState();
  const now = Date.now();
  const elapsed = now - state.lastOnlineAt;

  if (elapsed < 60_000) return; // Less than 1 min

  const offlineIp = calculateOfflineIp(state, elapsed);
  if (offlineIp <= 0) return;

  // Show offline popup
  showOfflinePopup(core, elapsed, offlineIp);
}

function showOfflinePopup(
  core: ReturnType<typeof createGameCore>,
  elapsed: number,
  offlineIp: number
) {
  const overlay = document.createElement('div');
  overlay.className = 'modal-overlay';

  const hours = Math.floor(elapsed / 3600_000);
  const minutes = Math.floor((elapsed % 3600_000) / 60_000);
  const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

  overlay.innerHTML = `
    <div class="modal">
      <div class="modal-title">${t('offline_title')}</div>
      <div class="modal-body">
        <div style="margin-bottom:8px">${t('offline_elapsed')}: ${timeStr}</div>
        <div style="font-size:20px;color:var(--accent);font-weight:700">+${formatNumber(offlineIp)}</div>
        <div style="margin-top:4px;font-size:12px;color:var(--text-dim)">${t('offline_earned')}</div>
      </div>
      <div class="modal-btns" style="flex-direction:column">
        <button class="modal-btn primary" id="offline-claim">${t('offline_claim')}</button>
        <button class="ad-btn" id="offline-double" style="margin-top:0">
          ${t('offline_double').replace('{amount}', formatNumber(offlineIp * 2))}
        </button>
      </div>
    </div>
  `;

  document.body.appendChild(overlay);

  document.getElementById('offline-claim')?.addEventListener('click', () => {
    core.dispatch({ type: 'ADD_IP', payload: { amount: offlineIp } });
    overlay.remove();
    showToast('+' + formatNumber(offlineIp) + ' IP');
  });

  document.getElementById('offline-double')?.addEventListener('click', async () => {
    await AdManager.showRewardedAd(
      { type: 'ip', amount: offlineIp * 2, description: t('ad_offline_double') },
      () => {
        core.dispatch({ type: 'ADD_IP', payload: { amount: offlineIp * 2 } });
        core.dispatch({ type: 'AD_WATCHED', payload: { rewardType: 'offline_double' } });
        overlay.remove();
        showToast('+' + formatNumber(offlineIp * 2) + ' IP');
      },
      () => {
        core.dispatch({ type: 'ADD_IP', payload: { amount: offlineIp } });
        overlay.remove();
        showToast('+' + formatNumber(offlineIp) + ' IP');
      }
    );
  });
}

// === Save/Load ===
function saveState(core: ReturnType<typeof createGameCore>) {
  try {
    const state = core.getState();
    const saveData = { ...state, lastOnlineAt: Date.now() };
    localStorage.setItem(SAVE_KEY, JSON.stringify(saveData));
  } catch { /* ignore */ }
}

function loadState(core: ReturnType<typeof createGameCore>) {
  try {
    const raw = localStorage.getItem(SAVE_KEY);
    if (!raw) return;

    const saved = JSON.parse(raw);
    // Merge with defaults to handle new fields
    const defaults = core.getState();
    const merged = { ...defaults, ...saved };

    // Deep-merge nested objects that may have new fields
    if (saved.faction) {
      merged.faction = { ...defaults.faction, ...saved.faction };
      if (saved.faction.relations) {
        merged.faction.relations = { ...defaults.faction.relations, ...saved.faction.relations };
      }
    }
    if (saved.boss) merged.boss = { ...defaults.boss, ...saved.boss };
    if (saved.agentCollection) merged.agentCollection = { ...defaults.agentCollection, ...saved.agentCollection };
    if (saved.conquest) merged.conquest = { ...defaults.conquest, ...saved.conquest };
    if (saved.infiltration) {
      merged.infiltration = { ...defaults.infiltration, ...saved.infiltration };
      merged.infiltration.baseStats = { ...defaults.infiltration.baseStats, ...saved.infiltration.baseStats };
    }

    // Ensure faction nextEventAt is valid (migration for saves before faction events)
    if (!merged.faction.nextEventAt) {
      merged.faction.nextEventAt = Date.now() + 30_000; // first event in 30s
    }

    // Reset the core state
    core.dispatch({ type: 'RESET' });
    // Override with saved state
    (core as any).state = merged;
  } catch (e) {
    console.warn('Failed to load save:', e);
  }
}

// === Start ===
main();
